/**
 * Desenhar uma barra.
 *
 * Em ESC/POS o firmware da impressora desenha o código de barras: `escpos.ts`
 * manda `GS k` com os caracteres e o resto é problema dela. Em HTML não há
 * firmware — se este arquivo não souber onde cada barra começa e termina, não
 * há barra nenhuma.
 *
 * ## A regra desta casa que vale em dobro aqui
 *
 * Um código de barras errado **não parece errado**. Ele imprime bonito, o
 * leitor do PDV lê sem reclamar, e o produto que aparece na tela é outro. Não
 * existe "deu erro" — existe a venda errada, descoberta no fim do mês pelo
 * estoque que não bate. Por isso os testes deste arquivo conferem padrão a
 * padrão contra valores checados à mão, e não "gerou alguma coisa".
 *
 * ## O que sai daqui
 *
 * Larguras de módulo, não pixels e não SVG. Um "módulo" é a barra mais fina do
 * símbolo; tudo no código de barras é um múltiplo inteiro dela. Quem desenha
 * decide quantos milímetros vale um módulo — e é assim que o mesmo símbolo
 * serve para um adesivo de 30 mm e para uma folha A4 sem recalcular nada.
 *
 * A lista alterna **começando sempre por barra**: `[2, 1, 1, 2, …]` é barra de
 * dois módulos, espaço de um, barra de um, espaço de dois. Os dois símbolos
 * começam com barra, então não há bandeira para dizer quem começa com o quê.
 */
const sum = (values) => values.reduce((total, value) => total + value, 0);
/* ------------------------------------------------------------------ CODE128 */
/**
 * Os 107 padrões do CODE128, em larguras de módulo.
 *
 * Cada um tem seis elementos (três barras e três espaços) somando 11 módulos —
 * menos a parada, que tem sete e soma 13. A tabela é a da ISO/IEC 15417 e não
 * se deriva de nada: é decorada, e é por isso que existe um teste que soma 11
 * em todas as 106 primeiras linhas. Um dígito trocado aqui é exatamente o
 * defeito que não dá erro.
 */
// prettier-ignore
const CODE128_PATTERNS = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213',
    '122312', '132212', '221213', '221312', '231212', '112232', '122132',
    '122231', '113222', '123122', '123221', '223211', '221132', '221231',
    '213212', '223112', '312131', '311222', '321122', '321221', '312212',
    '322112', '322211', '212123', '212321', '232121', '111323', '131123',
    '131321', '112313', '132113', '132311', '211313', '231113', '231311',
    '112133', '112331', '132131', '113123', '113321', '133121', '313121',
    '211331', '231131', '213113', '213311', '213131', '311123', '311321',
    '331121', '312113', '312311', '332111', '314111', '221411', '431111',
    '111224', '111422', '121124', '121421', '141122', '141221', '112214',
    '112412', '122114', '122411', '142112', '142211', '241211', '221114',
    '413111', '241112', '134111', '111242', '121142', '121241', '114212',
    '124112', '124211', '411212', '421112', '421211', '212141', '214121',
    '412121', '111143', '111341', '131141', '114113', '114311', '411113',
    '411311', '113141', '114131', '311141', '411131',
    '211412', // 103 — início A
    '211214', // 104 — início B
    '211232', // 105 — início C
    '2331112', // 106 — parada, com a barra final de dois módulos
];
const CODE128_START_B = 104;
const CODE128_START_C = 105;
const CODE128_STOP = 106;
/**
 * Uma linha da tabela, e um erro em vez de um símbolo pela metade.
 *
 * `noUncheckedIndexedAccess` obriga a dizer o que acontece fora da tabela, e a
 * resposta certa é parar: um valor fora de 0–106 só chega aqui por defeito no
 * cálculo acima, e um código de barras incompleto imprime igualmente bem.
 */
const at = (table, index, what) => {
    const pattern = table[index];
    if (pattern === undefined) {
        throw new Error(`${what}: valor ${String(index)} fora da tabela.`);
    }
    return pattern;
};
const patternModules = (value) => {
    const pattern = at(CODE128_PATTERNS, value, 'CODE128');
    const widths = [];
    // `for…of` sobre a string, e não `[...pattern]`: espalhar decompõe caractere
    // composto, e a regra de lint que proíbe isso está certa em geral — aqui a
    // tabela é ASCII de um dígito, mas a exceção custaria mais que o laço.
    for (const digit of pattern) {
        widths.push(Number(digit));
    }
    return widths;
};
/** Só dígito, e em quantidade par — a condição para o subconjunto C caber. */
const fitsSubsetC = (data) => data.length >= 4 && data.length % 2 === 0 && /^[0-9]+$/.test(data);
/**
 * CODE128 do que vier — o SKU, quase sempre.
 *
 * **Um subconjunto só por símbolo, sem trocar no meio.** Trocar de B para C
 * quando aparece um trecho de dígitos economiza uns milímetros de adesivo e
 * acrescenta o caso de borda mais caro da codificação. Num adesivo de 50 mm com
 * um SKU de dez caracteres não falta espaço; então: tudo dígito e em quantidade
 * par vira C (metade das barras), qualquer outra coisa vira B.
 *
 * B cobre ASCII 32–126. Fora disso não há o que codificar sem inventar, e
 * inventar aqui é imprimir outro código — então recusa.
 */
export const encodeCode128 = (data) => {
    if (data === '')
        throw new Error('Código de barras sem dado.');
    const values = [];
    if (fitsSubsetC(data)) {
        values.push(CODE128_START_C);
        for (let index = 0; index < data.length; index += 2) {
            values.push(Number(data.slice(index, index + 2)));
        }
    }
    else {
        values.push(CODE128_START_B);
        for (const character of data) {
            const code = character.codePointAt(0) ?? 0;
            if (code < 32 || code > 126) {
                throw new Error(`CODE128 subconjunto B não codifica ${JSON.stringify(character)}.`);
            }
            // O valor B de um caractere imprimível é o seu ASCII menos 32.
            values.push(code - 32);
        }
    }
    /*
     * O dígito de verificação: início + Σ(posição × valor), módulo 103. A posição
     * começa em 1 no primeiro caractere DEPOIS do início — e o próprio início
     * entra com peso 1, que é o mesmo que somá-lo uma vez.
     */
    let checksum = 0;
    values.forEach((value, index) => {
        // O próprio início entra com peso 1, que é o mesmo que somá-lo uma vez.
        checksum += (index === 0 ? 1 : index) * value;
    });
    values.push(checksum % 103, CODE128_STOP);
    const modules = values.flatMap(patternModules);
    return { modules, totalModules: sum(modules), text: data };
};
/* ------------------------------------------------------------------- EAN-13 */
/** Ímpar/esquerda. Cada dígito são sete módulos, `1` barra e `0` espaço. */
// prettier-ignore
const EAN_L = [
    '0001101', '0011001', '0010011', '0111101', '0100011',
    '0110001', '0101111', '0111011', '0110111', '0001011',
];
/** Par/esquerda — o R de trás para frente. */
// prettier-ignore
const EAN_G = [
    '0100111', '0110011', '0011011', '0100001', '0011101',
    '0111001', '0000101', '0010001', '0001001', '0010111',
];
/** Direita — o complemento do L, e a única metade que começa com barra. */
// prettier-ignore
const EAN_R = [
    '1110010', '1100110', '1101100', '1000010', '1011100',
    '1001110', '1010000', '1000100', '1001000', '1110100',
];
/**
 * O primeiro dígito não tem barras próprias.
 *
 * Ele é escrito no **padrão de paridade** dos seis dígitos da esquerda — qual
 * deles usa a tabela L e qual usa a G. É por isso que um EAN-13 tem doze
 * dígitos desenhados e treze impressos, e por isso que o leitor sabe de que
 * lado o símbolo entrou no feixe.
 */
// prettier-ignore
const EAN_PARITY = [
    'LLLLLL', 'LLGLGG', 'LLGGLG', 'LLGGGL', 'LGLLGG',
    'LGGLLG', 'LGGGLL', 'LGLGLG', 'LGLGGL', 'LGGLGL',
];
/** Transforma `'1011001'` em `[1, 1, 2, 2, 1]`, começando por barra. */
const runLengths = (bits) => {
    const runs = [];
    // Começa contando barra. O símbolo começa com a guarda `101`, que é barra —
    // se um dia começasse com espaço, este zero inicial diria isso ao desenhista.
    let current = '1';
    let length = 0;
    for (const bit of bits) {
        if (bit === current) {
            length += 1;
            continue;
        }
        runs.push(length);
        current = bit;
        length = 1;
    }
    runs.push(length);
    return runs;
};
/**
 * O dígito de verificação do GTIN, **conferido e não calculado**.
 *
 * Quem calcula é `gtinCheckDigit`, no `@aeris/catalog`, e continua sendo o
 * único dono do cálculo — este pacote não importa o catálogo e não devolve
 * dígito nenhum. O que ele faz é recusar: se os treze dígitos que chegaram aqui
 * não fecham, o símbolo não é impresso. É a última porta antes do papel, e um
 * EAN inválido impresso é um adesivo que o marketplace rejeita e o leitor
 * aceita.
 */
const eanChecksumHolds = (digits) => {
    let total = 0;
    for (let index = 0; index < 12; index += 1) {
        total += Number(digits[index]) * (index % 2 === 0 ? 1 : 3);
    }
    return (10 - (total % 10)) % 10 === Number(digits[12]);
};
/**
 * O número é um EAN-13 de verdade? — F168.
 *
 * Perguntar ANTES de escolher a simbologia, em vez de tentar e cair no `catch`:
 * o código das etiquetas desta loja vem do sistema antigo e não tem dígito
 * verificador, e `encodeEan13` lança — numa folha de etiquetas, isso era o papel
 * inteiro não sair.
 */
export const isEan13 = (data) => /^[0-9]{13}$/.test(data) && eanChecksumHolds(data);
export const encodeEan13 = (digits13) => {
    if (!/^[0-9]{13}$/.test(digits13)) {
        throw new Error('EAN-13 precisa de exatamente 13 dígitos.');
    }
    if (!eanChecksumHolds(digits13)) {
        throw new Error(`EAN-13 com dígito verificador inválido: ${digits13}.`);
    }
    const parity = at(EAN_PARITY, Number(digits13[0]), 'EAN-13 paridade');
    let left = '';
    for (let index = 0; index < 6; index += 1) {
        const digit = Number(digits13[index + 1]);
        left += at(parity[index] === 'L' ? EAN_L : EAN_G, digit, 'EAN-13 esquerda');
    }
    let right = '';
    for (let index = 7; index < 13; index += 1) {
        right += at(EAN_R, Number(digits13[index]), 'EAN-13 direita');
    }
    // 3 + 42 + 5 + 42 + 3 = 95 módulos. A guarda central existe para o leitor
    // saber onde a metade da direita começa mesmo lendo o símbolo de trás.
    const bits = `101${left}01010${right}101`;
    const modules = runLengths(bits);
    return { modules, totalModules: sum(modules), text: digits13 };
};
//# sourceMappingURL=barcode.js.map